import React, {
  FunctionComponent,
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Stack,
  Alert,
  AlertTitle,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Modal,
  ListItemText,
  Tooltip,
  Snackbar,
  Chip,
  Autocomplete,
} from '@mui/material';
import { AxiosError, AxiosResponse } from 'axios';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CircularProgress from '@mui/material/CircularProgress';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {
  getLetterPrefill,
  postDownloadModifiedLetterTemplate,
  getSentLetters,
  postDownloadSentLetter,
  postSendModifiedLetterTemplate,
  getLetterTemplatesListByCompany,
  updateClaimVehicleMileageCPE,
} from '@shared/hooks/useStoreToCPE';
import {
  LetterPrefill,
  LetterTemplate,
  Recipient,
  SentLetter,
  SentLetters,
  RequestErrorAlertProp,
  getBadRequestMessage,
  BadRequestMessageWarning,
  BadRequestMessage,
} from '@src/types/letters';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useHistory } from 'react-router';
import { Formik, Form } from 'formik';
import { isEqual, set } from 'lodash';
import * as Yup from 'yup';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import * as API from '@services/claims';
import {
  IInspectionContext,
  InspectionContext,
  downloadEvent,
  finishEvent,
  getClaimById,
  getReasonsPendingInfo,
  sendActiveLink,
  sendClientPdf,
} from '@context/inspections';
import { FormDataFilter } from '@shared/utils/filter-data';
import {
  FieldTypes,
  IClaimForm,
  StepStatus,
  CurrentStatusText,
  IClaim,
  ClaimantType,
  StageIds,
  StatusIds,
} from '@src/types/claimsTracker';
import {
  BrandsId,
  CountryCode,
  ReasonPendingInfo,
  Rol,
  TransitionStatesIds,
  ObjetedStatus,
  IsStepEnabledForEdit,
  isPurdyNoProcedeAvailable,
} from '@src/types/common';
import Loader from '@components/common/loader/Loader';
import Photos from '../customTargets/photos';
import ClaimEstimated from '../estimated';
import Depreciation from '../depreciation';
import FormFieldsComponent from '../form/FormFieldsComponent';
import Button from '../button';
import ClaimExpenses from '../expense';
import AgreementDetail from '../agreementDetail';
import DialogComponent from '@components/common/dialog';
import './index.scss';
import { ActionType } from '@src/types/action';
import ClaimRequest from '@views/ClaimsTracker/claimRequest';
import TotalLoss from '../totalLoss';
import Subrogation from '../subrogation';
import FinalResolution from '../subrogationFinalResolution';
import EventTracking from '../subrogationEventTracking';
import Reserve from '../reserve';
import { keyValueStructure, transformFormValueByType } from '@shared/utils';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import ThirdsForm from '../thirds/Thirds';
import CATALOGS from '@static/constants/catalogs';
import ClaimAutoParts from '../autoParts';
import { sinisterClaims } from '@context/inspections/actions/actions';
import { getPendingReasonsByCountry } from '@static/constants/pending-reasons';
import CircunstancesFormClaims from '../circunstances/CircunstancesFormClaims';
import { SelectOption } from '@src/types/select-option-inspection';
import roles from '@static/constants/roles';
import { IClaimState } from '@src/types/claim';
import { IFlag } from '../information/CPEFlags';
import {
  runDefaultBehavior,
  stageHandlers,
  StageHandlerContext,
} from './stageHandlers';
import PurdyEstimationComponent from '../estimated/purdyEstimation/PurdyEstimationComponent';

// Extender dayjs con los plugins
dayjs.extend(utc);
dayjs.extend(timezone);

interface ClaimsSections {
  claimUId: string;
  sections: any;
  legacyCurrentClaim?: IClaim;
  setLegacyCurrentClaim?: (data: IClaim) => void;
  currentClaimForm?: IClaimForm;
  steps: SelectOption[];
  CPEClaim: IClaimState;
  inspectionFlags?: IFlag[];
  setInspectionFlags?: React.Dispatch<React.SetStateAction<IFlag[]>>;
  cpeIds?: {
    cpeClaimId: string;
    cpeDamageAnalysisId: string;
  };
}

interface OptionStep {
  IsFinishedWithoutEstimated: boolean;
  IsClaimNotApplicable: boolean;
  RequireInformation: boolean;
  Objeted: boolean;
  ChangeStatusRevert: boolean;
}

interface NewStatus {
  StageId: number;
  StatusId: number;
}

interface Status {
  newStatus: NewStatus;
  text: string;
}

type typeStatus = {
  [key in CountryCode]: Status[];
};

const listStatus: typeStatus = {
  [CountryCode.COL]: [
    {
      newStatus: {
        StageId: StageIds.DAMAGE_ANALYSIS,
        StatusId: StatusIds.PENDING_MEDICAL_AUDIT,
      },
      text: 'Auditoría Médica',
    },
    {
      newStatus: {
        StageId: StageIds.DAMAGE_ANALYSIS,
        StatusId: StatusIds.PENDING_LEGAL_CONCEPT,
      },
      text: 'Concepto Jurídico',
    },
    {
      newStatus: {
        StageId: StageIds.DAMAGE_ANALYSIS,
        StatusId: StatusIds.PENDING_ADJUSTMENT,
      },
      text: 'Estimado/Ajuste',
    },
  ],
  [CountryCode.PRI]: [
    {
      newStatus: {
        StageId: StageIds.INSPECT_SECTION,
        StatusId: StatusIds.PENDING_AUDIT,
      },
      text: 'Auditoría',
    },
    {
      newStatus: {
        StageId: StageIds.DAMAGE_ANALYSIS,
        StatusId: StatusIds.PENDING_ESTIMATE,
      },
      text: 'Estimado',
    },
    {
      newStatus: {
        StageId: StageIds.DAMAGE_ANALYSIS,
        StatusId: StatusIds.PENDING_ADJUSTMENT,
      },
      text: 'Ajuste',
    },
  ],
  [CountryCode.PAN]: [],
  [CountryCode.CRC]: [],
};

const ClaimSections: FunctionComponent<ClaimsSections> = ({
  claimUId,
  sections,
  legacyCurrentClaim,
  setLegacyCurrentClaim,
  currentClaimForm,
  steps,
  CPEClaim,
  inspectionFlags,
  setInspectionFlags,
  cpeIds,
}): JSX.Element => {
  const history = useHistory();
  const formikRef = useRef<any>(null);
  const { state, dispatch } = useContext<IInspectionContext>(InspectionContext);
  const [initialValues, setInitialValues] = useState<any>({});
  const [expanded, setExpanded] = useState<{ [key: number]: boolean }>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [validationSchema, setValidationSchema] = useState<any>(null);
  const [showConfirmSubmit, setShowConfirmSubmit] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDialogMsg, setOpenDialogMsg] = useState('');
  const [isDetectedChanges] = useState<boolean>(false);
  const [isDetectedError, setIsDetectedError] = useState<boolean>(false);
  const [msgError, setMsgError] = useState<any>(null);
  const [saveInProcess, setSaveInProcess] = useState<Date>(new Date());
  const [isFinishDisableByCondition, setIsFinishDisableByCondition] = useState<
    boolean
  >(false);
  const [optionsStep, setOptionStep] = useState<OptionStep>({
    IsClaimNotApplicable: false,
    IsFinishedWithoutEstimated: false,
    RequireInformation: false,
    Objeted: false,
    ChangeStatusRevert: false,
  });

  const [reasonStatusChange, setReasonStatusChange] = useState<string>('');
  const [reasonSelect, setReasonSelect] = useState<string>();
  const [textNotification, setTextNotification] = useState<string>('');
  const [statusSelected, setStatusSelected] = useState<NewStatus | undefined>();

  // Obtener país del usuario y razones pendientes
  const currentCountryCode = localStorage.getItem(
    'currentCountryCode',
  ) as CountryCode;
  const pendingReasons = getPendingReasonsByCountry(
    currentCountryCode || CountryCode.CRC,
  );
  const [isDisabledButtonConfirm, setIsDisabledButtonConfirm] = useState<
    boolean
  >(false);
  const [confirmActiveLink, setConfirmActiveLink] = useState<boolean>(false);
  const [, setFinishBtnIsDisable] = useState(true);
  const [isFirstRender, setIsFirstRender] = useState(true);
  const logString = 'ActionMenu';
  const [didRequestFail, setDidRequestFail] = useState(false);
  const [didRequestThrow, setDidRequestThrow] = useState(false);
  const [requestResponse, setRequestResponse] = useState<AxiosResponse>();
  const [requestError, setRequestError] = useState<unknown>('Error:');
  const [errorAlert, setErrorAlert] = useState<BadRequestMessage>({
    message: BadRequestMessageWarning,
    error: '',
  });
  const unsuccessfulRequest = useRef<
    | 'getLetterTemplatesList'
    | 'getLetterPrefill'
    | 'postDownloadSentLetter_Preview'
    | 'postDownloadSentLetter_Download'
    | 'postDownloadModifiedLetterTemplate_Download'
    | 'postDownloadModifiedLetterTemplate_Send'
    | null
  >(null);
  const [modalTitle, setModalTitle] = useState<string>('');
  const [isLettersModalOpen, setIsLettersModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [canGoBack, setCanGoBack] = useState(false);
  const [sentLetters, setSentLetters] = useState<SentLetter[]>([]);
  const [isSentLettersFailing, setIsSentLettersFailing] = useState(false);
  const [resfreshSentLetters, setRefreshSentLetters] = useState(false);
  const [isSentLetterPreviewClicked, setIsSentLetterPreviewClicked] = useState(
    false,
  );
  const [
    isSentLetterDownloadClicked,
    setIsSentLetterDownloadClicked,
  ] = useState(false);
  const [isSentLetterDownloading, setIsSentLetterDownloading] = useState(false);
  const [isTemplateMenuSelected, setIsTemplateMenuSelected] = useState(false);
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplate[]>([]);
  const [isLetterSelected, setIsLetterSelected] = useState(false);
  const [selectedLetterTemplate, setSelectedLetterTemplate] = useState<
    LetterTemplate
  >();
  const [claimIdForLetter, setClaimIdForLetter] = useState<string>('');
  const [prefilledLetter, setPrefilledLetter] = useState<LetterPrefill>();
  const [prefillMatchesSelection, setPrefillMatchesSelection] = useState(false);
  const [previousSendSelection, setPreviousSendSelection] = useState<string>(
    'insuredPerson',
  );
  const [previousPersonType, setPreviousPersonType] = useState<string>(
    '(asegurado)',
  );
  const [requiresPrevious, setRequiresPrevious] = useState(false);
  const templateSubmitAction = useRef<'download' | 'send' | null>(null);
  const [
    isTemplateLetterDownloadClicked,
    setIsTemplateLetterDownloadClicked,
  ] = useState(false);
  const [
    isTemplateLetterDownloading,
    setIsTemplateLetterDownloading,
  ] = useState(false);
  const [
    isTemplateLetterSendClicked,
    setIsTemplateLetterSendClicked,
  ] = useState(false);
  const [isTemplateLetterSending, setIsTemplateLetterSending] = useState(false);

  const returnToTemplateSelectionMenu = () => {
    console.log(logString, 'Returned to template selection');
    setPreviousSendSelection('insuredPerson');
    setPreviousPersonType('(asegurado)');
    setRequiresPrevious(false);
    setIsLetterSelected(false);
    setSelectedLetterTemplate(undefined);
    setPrefilledLetter(undefined);
    templateSubmitAction.current = null;
    setIsTemplateLetterDownloadClicked(false);
    setIsTemplateLetterDownloading(false);
    setIsTemplateLetterSendClicked(false);
    setIsTemplateLetterSending(false);
    setModalTitle('- Plantillas de Cartas');
  };

  const returnToMainMenu = () => {
    console.log(logString, 'Returned to main menu');
    setCanGoBack(false);
    returnToTemplateSelectionMenu();
    setIsTemplateMenuSelected(false);
    setModalTitle('');
  };

  const closeErrorModal = () => {
    console.log(logString, 'Closed error modal');
    setIsErrorModalOpen(false);
    unsuccessfulRequest.current = null;
    setDidRequestFail(false);
    setDidRequestThrow(false);
    setRequestResponse(undefined);
    setRequestError('Error:');
  };

  const handleGoBack = () => {
    console.log(logString, 'Went back');
    if (isLetterSelected) {
      returnToTemplateSelectionMenu();
    } else if (isTemplateMenuSelected) {
      returnToMainMenu();
    }
  };

  const resetMainModal = () => {
    console.log(logString, 'Reset main modal');
    setIsLettersModalOpen(false);
    returnToMainMenu();
    closeErrorModal();
  };

  const openTemplateMenu = () => {
    console.log(logString, 'Open template selection menu');
    setIsTemplateMenuSelected(true);
    setModalTitle('- Plantillas de Cartas');
    setCanGoBack(true);
  };

  const selectLetterTemplate = (template: LetterTemplate) => {
    console.log(logString, 'Selected letter template');
    setIsLetterSelected(true);
    setModalTitle(
      `- Plantillas de Cartas - Plantilla: ${template.spanishName}`,
    );
    setSelectedLetterTemplate(template);
    setCanGoBack(true);
  };

  useEffect(() => {
    if (didRequestFail || didRequestThrow) {
      console.error(
        logString,
        'Request failed or threw: ',
        unsuccessfulRequest.current,
      );
      setIsErrorModalOpen(true);
    } else if (!didRequestFail && !didRequestThrow) {
      console.log(
        logString,
        'Request was succesful: ',
        unsuccessfulRequest.current,
      );
      setIsErrorModalOpen(false);
    }
  }, [didRequestFail, didRequestThrow]);

  useEffect(() => {
    setErrorAlert(
      getBadRequestMessage(
        unsuccessfulRequest.current,
        requestResponse,
        requestError,
      ),
    );
  }, [unsuccessfulRequest, requestResponse, requestError]);

  useEffect(() => {
    if (prefilledLetter?.templateId === selectedLetterTemplate?.id) {
      console.log(logString, 'Selected letter template can be rendered');
      setPrefillMatchesSelection(true);
    } else {
      console.error(logString, 'Selected letter template cannot be rendered');
      setPrefillMatchesSelection(false);
    }
  }, [prefilledLetter, isLetterSelected, selectedLetterTemplate]);

  useEffect(() => {
    const fetchClaimIdForLetter = async () => {
      if (cpeIds?.cpeClaimId) {
        const claimId = cpeIds.cpeClaimId;

        console.log(logString, 'ClaimId: ', claimId);
        setClaimIdForLetter(claimId);
      } else {
        console.warn(logString, 'No claimId found');
        setClaimIdForLetter('');
      }

      // Fetch letters sent to insured person
      try {
        if (!cpeIds?.cpeClaimId || cpeIds?.cpeClaimId.length === 0) {
          console.warn(logString, 'No CPE ClaimId found');
          return;
        }

        const letters = await getSentLetters(cpeIds?.cpeClaimId ?? '');
        if (letters?.status === 200) {
          console.log(logString, 'Fetched sent letters succesfully');
          setSentLetters(letters.data);
          setIsSentLettersFailing(false);
        } else {
          console.warn(
            logString,
            'Could not fetch sent letters: ',
            letters?.data,
          );
          setIsSentLettersFailing(true);
        }
      } catch (e) {
        console.error(logString, 'Error fetching sent letters: ', e);
        setRequestError(e);
        setRequestResponse(
          (e as AxiosError).response ? (e as AxiosError).response : undefined,
        );
        setIsSentLettersFailing(true);
      }
    };
    const fetchLetterTemplates = async () => {
      try {
        console.info(
          'legacyCurrentClaim',
          legacyCurrentClaim?.jsonFieldData?.event.CompanyId,
        );
        if (!legacyCurrentClaim?.jsonFieldData?.event.CompanyId) {
          console.warn(logString, 'No companyId found');
          return;
        }

        const templates = await getLetterTemplatesListByCompany(
          legacyCurrentClaim?.jsonFieldData?.event.CompanyId as BrandsId,
        );
        if (templates?.status === 200) {
          console.log(logString, 'Got letter templates list');
          unsuccessfulRequest.current = null;
          setDidRequestFail(false);
          setLetterTemplates(templates.data.templates);
        } else {
          console.warn(
            logString,
            'Could not get letter templates list: ',
            templates?.data,
          );
          unsuccessfulRequest.current = 'getLetterTemplatesList';
          setDidRequestFail(true);
        }
      } catch (e) {
        console.error(logString, 'Error getting letter templates list: ', e);
        setRequestError(e);
        setRequestResponse(
          (e as AxiosError).response ? (e as AxiosError).response : undefined,
        );
        unsuccessfulRequest.current = 'getLetterTemplatesList';
        setDidRequestThrow(true);
      }
    };

    fetchClaimIdForLetter();
    fetchLetterTemplates();
  }, [cpeIds?.cpeClaimId, legacyCurrentClaim?.jsonFieldData?.event.CompanyId]);

  useEffect(() => {
    const fetchSentLetters = async () => {
      try {
        if (!cpeIds?.cpeClaimId || cpeIds?.cpeClaimId.length === 0) {
          console.warn(logString, 'No CPE ClaimId found');
          return;
        }

        const letters = await getSentLetters(cpeIds?.cpeClaimId ?? '');
        if (letters?.status === 200) {
          console.log(logString, 'Refreshed sent letters');
          setSentLetters(letters.data);
          setIsSentLettersFailing(false);
        } else {
          console.warn(
            logString,
            'Could not refresh sent letters: ',
            letters?.data,
          );
          setIsSentLettersFailing(true);
        }
      } catch (e) {
        console.error(logString, 'Error refreshing sent letters: ', e);
        setRequestError(e);
        setRequestResponse(
          (e as AxiosError).response ? (e as AxiosError).response : undefined,
        );
        setIsSentLettersFailing(true);
      }
    };

    fetchSentLetters();
    setRefreshSentLetters(false);
  }, [resfreshSentLetters]);

  useEffect(() => {
    const fetchLetterPrefill = async () => {
      if (selectedLetterTemplate && claimIdForLetter) {
        try {
          const prefill = await getLetterPrefill(
            selectedLetterTemplate.id,
            claimIdForLetter,
          );
          if (prefill?.status === 200) {
            console.log(logString, 'Fetched letter prefill');
            unsuccessfulRequest.current = null;
            setDidRequestFail(false);
            setPrefilledLetter(prefill.data);
            setRequiresPrevious(true);
          } else {
            console.warn(
              logString,
              'Could not fetch letter prefill: ',
              prefill?.data,
            );
            unsuccessfulRequest.current = 'getLetterPrefill';
            setDidRequestFail(true);
          }
        } catch (e) {
          console.error(logString, 'Error fetching letter prefill: ', e);
          setRequestError(e);
          unsuccessfulRequest.current = 'getLetterPrefill';
          setRequestResponse(
            (e as AxiosError).response ? (e as AxiosError).response : undefined,
          );
          setDidRequestThrow(true);
        }
      } else {
        setPrefilledLetter(undefined);
      }
    };

    fetchLetterPrefill();
  }, [selectedLetterTemplate, claimIdForLetter]);

  const customStyle = {
    padding: '8px 16px 8px 16px',
    height: 'auto',
    gap: '8px',
  };
  const userRole = Number(localStorage.getItem('rolId'));
  const [reasonPendingInfo, setReasonPendingInfo] = React.useState<
    ReasonPendingInfo[]
  >([]);

  const [pendingSideEffect, setPendingSideEffect] = useState<{
    sideEffect:
      | ((claimId: string, context?: StageHandlerContext) => Promise<void>)
      | null;
    claimId: string | null;
    context?: StageHandlerContext;
  }>({ sideEffect: null, claimId: null });

  const isDisabled =
    (Number(localStorage.getItem('rolId')) === Rol.INSURER &&
      !IsStepEnabledForEdit(
        legacyCurrentClaim?.jsonFieldData?.event.CompanyId as BrandsId,
        currentClaimForm?.info.currentStepStatus as StepStatus,
      )) ||
    Number(localStorage.getItem('rolId')) === Rol.CUSTOMER ||
    Number(localStorage.getItem('rolId')) === Rol.CLAIMS_ASSISTANT;

  const condition1_INSURER =
    Number(localStorage.getItem('rolId')) === Rol.INSURER &&
    currentClaimForm?.info.currentStepStatus !==
      StepStatus.PENDING_INSURANCE_PROCESSED &&
    !IsStepEnabledForEdit(
      legacyCurrentClaim?.jsonFieldData?.event.CompanyId as BrandsId,
      currentClaimForm?.info.currentStepStatus as StepStatus,
    );

  const condition2_INSPECTOR =
    Number(localStorage.getItem('rolId')) === Rol.INSPECTOR &&
    currentClaimForm?.info.currentStepStatus !== StepStatus.PENDING_INSPECT;

  const condition3_CSM =
    legacyCurrentClaim?.jsonFieldData?.event.CompanyId === BrandsId.CSM &&
    legacyCurrentClaim?.jsonFieldData.event.IndEstimate === '2' &&
    currentClaimForm?.info.currentStepStatus === StepStatus.PENDING_AUDIT;

  const isDisabledButton =
    condition1_INSURER || condition2_INSPECTOR || condition3_CSM;

  const pendingApproval =
    currentClaimForm?.info.currentStepStatus === StepStatus.PENDING_APPROVAL;

  const [
    showFinishWithoutEstimatedButton,
    setShowFinishWithoutEstimatedButton,
  ] = useState(
    currentClaimForm?.info.currentStepStatus === StepStatus.PENDING_AUDIT &&
      Number(legacyCurrentClaim?.accountReference) === BrandsId.CSM &&
      (userRole === Rol.ADJUSTER ||
        userRole === Rol.ADMIN ||
        userRole === Rol.AUDIT),
  );

  const showButtonNotApplicable =
    (currentClaimForm?.info.currentStepStatus ===
      StepStatus.PENDING_DAMAGE_ANALYSIS &&
      currentClaimForm?.info.countryCode === CountryCode.COL) ||
    ((currentClaimForm?.info.currentStepStatus ===
      StepStatus.PENDING_ANALYSIS ||
      currentClaimForm?.info.currentStepStatus ===
        StepStatus.PENDING_PRE_ANALYSIS) &&
      currentClaimForm?.info.countryCode === CountryCode.CRC) ||
    isPurdyNoProcedeAvailable(
      Number(legacyCurrentClaim?.accountReference) as BrandsId,
      currentClaimForm?.info.countryCode,
      currentClaimForm?.info.currentStepStatus,
    );

  const showButtonPendingInfo =
    currentClaimForm?.info.currentStepStatus === StepStatus.PENDING_AUDIT &&
    (currentClaimForm?.info.countryCode === CountryCode.COL ||
      currentClaimForm?.info.countryCode === CountryCode.PAN ||
      currentClaimForm?.info.countryCode === CountryCode.PRI ||
      Number(legacyCurrentClaim?.accountReference) === BrandsId.PURDY);

  const showButtonChangeStatus =
    Number(StatusIds[currentClaimForm?.info.currentStepStatus]) >
      StatusIds.PENDING_AUDIT &&
    currentClaimForm?.info.countryCode === CountryCode.PRI &&
    userRole === Rol.ADMIN;

  const showButtonObjeted =
    currentClaimForm?.info.currentStepStatus ===
      StepStatus.PENDING_APPRO_AGREE &&
    currentClaimForm?.info.countryCode === CountryCode.COL;

  const showFinishWithoutSinister =
    currentClaimForm?.info.currentStepStatus === StepStatus.PENDING_SINISTER &&
    (userRole === Rol.INSURER || userRole === Rol.ADMIN);

  const enabledByStates = [StepStatus.IN_PROCESS, StepStatus.PENDING].includes(
    sections.status,
  );

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const initOptionStep = () => {
    setOptionStep({
      IsFinishedWithoutEstimated: false,
      IsClaimNotApplicable: false,
      RequireInformation: false,
      Objeted: false,
      ChangeStatusRevert: false,
    });
  };

  const handleClickOptionStep = (event: any) => {
    loadReasonsPendingInfo(currentClaimForm?.info.countryId);
    initOptionStep();
    setIsDisabledButtonConfirm(false);
    setShowConfirmSubmit(true);
    optionsStep[event.target?.id] = true;

    if (!optionsStep.IsClaimNotApplicable && !optionsStep.Objeted) {
      setReasonStatusChange('');
      setStatusSelected(undefined);
    }

    if (!optionsStep.RequireInformation) {
      setTextNotification('');
    }

    setOptionStep(optionsStep);
  };

  const handleChangeStatus = (event: any) => {
    const [stageId, statusId] = String(event.target?.value).split('-');
    setStatusSelected({ StageId: Number(stageId), StatusId: Number(statusId) });
  };

  const handleChange = (panel: number) => (
    _: React.SyntheticEvent,
    newExpanded: boolean,
  ) => {
    setExpanded({ ...expanded, [panel]: newExpanded });
    if (expanded) {
      window.setTimeout(() => executeScroll(panel), 500);
    }
  };

  const executeScroll = (panel: number) => {
    const elementAccordion = document.getElementById(`panel${panel}-header`);
    elementAccordion?.scrollIntoView();
  };

  const formatDate = (utcDate: string, countryCode: CountryCode): string => {
    const timezones = {
      [CountryCode.PRI]: 'America/Puerto_Rico',
      [CountryCode.CRC]: 'America/Costa_Rica',
      [CountryCode.PAN]: 'America/Panama',
      [CountryCode.COL]: 'America/Bogota',
    };

    const formattedDate = dayjs
      .utc(utcDate)
      .tz(timezones[countryCode])
      .format(CATALOGS.getCountryFormat().dateTimeFormat + ' HH:mm:ss')
      .toUpperCase();

    return formattedDate === 'INVALID DATE' ? '' : formattedDate;
  };

  const validations = (data: any) => {
    let schema = Yup.string();

    if (data.isRequired && data.isVisible && !data.isReadOnly) {
      schema = schema.required('Campo requerido');
    }

    if (data.min) {
      schema = schema.min(data.min, `Mínimo ${data.min} caracteres`);
    }

    if (data.max) {
      schema = schema.max(data.max, `Máximo ${data.max} caracteres`);
    }

    if (data.regex) {
      schema = schema.matches(new RegExp(data.regex), 'Formato inválido');
    }

    return schema;
  };

  async function loadReasonsPendingInfo(CountryId: number) {
    const reasonsPendingInfoToLoad = await getReasonsPendingInfo(CountryId);
    if (reasonsPendingInfoToLoad) {
      setReasonPendingInfo(reasonsPendingInfoToLoad);
    }
  }

  const getValidationSchema = (sections: any) => {
    return Yup.object().shape(
      (sections.sections as any[])
        .flatMap((section) =>
          section.containers.flatMap((container) => container.fields),
        )
        .reduce((schema, field) => {
          let replace = field.modelBinding;
          if (field.modelBinding && field.modelBinding.includes('.')) {
            replace = field.modelBinding.replace('.', '&');
          }
          const fieldName = `${replace}`;
          schema[fieldName] = validations(field.constraints);

          return schema;
        }, {}),
    );
  };

  // Maps data from CPE claim object to corresponding Claim Tracker fields in "Informacion de Apertura"
  // Prevents duplicate data entry by reusing existing CPE information
  const getValueFromCPEClaim = (field: string, CPEClaim: IClaimState) => {
    const ctToCpeMap: { [key: string]: string } = {
      Reclamacion: CPEClaim?.claim?.claimNumber,
      'datosDelAccidente.fecha': CPEClaim?.incident?.incidentDate,
      'datosDelAccidente.lugar': CPEClaim?.incident?.location,
      'datosDelPolicia.querella': CPEClaim?.incident?.policeReportNumber,
      'datosDelAccidente.vehiculos':
        CPEClaim?.incident?.numberOfVehiclesInvolved,
      'datosDelPolicia.nombre': CPEClaim?.incident?.policeOfficerName,
      'datosDelPolicia.placa': CPEClaim?.incident?.policeBadgeNumber,
      'datosDelPolicia.cuartel': CPEClaim?.incident?.policeStationName,
      'datosDelPolicia.estuvo':
        CPEClaim?.incident?.wasPolicePresent === 'yes'
          ? '1'
          : CPEClaim?.incident?.wasPolicePresent === 'no'
          ? '0'
          : '',
      'datosVehiculo1.ocurrido': [
        CPEClaim?.incident?.additionalNotes,
        CPEClaim?.incident?.carDamages,
      ]
        .filter(Boolean)
        .join(' // '),
      'firmas.fechaDueno1': CPEClaim?.incident?.incidentDate,
    };

    if (CPEClaim?.personsInvolved.length === 1) {
      ctToCpeMap['datosVehiculo1.duenoDireccion1'] =
        CPEClaim?.personsInvolved[0]?.addresLine1;
      ctToCpeMap['datosVehiculo1.duenoCodigoPostal'] =
        CPEClaim?.personsInvolved[0]?.zipCode;
      ctToCpeMap['datosVehiculo1.duenoLicencia'] =
        CPEClaim?.personsInvolved[0]?.licenseNumber;
      ctToCpeMap['datosVehiculo1.conductorNombre'] =
        CPEClaim?.personsInvolved[0]?.name;
      ctToCpeMap['datosVehiculo1.conductorApellidos'] =
        CPEClaim?.personsInvolved[0]?.name;
      ctToCpeMap['datosVehiculo1.conductorLicencia'] =
        CPEClaim?.personsInvolved[0]?.licenseNumber;
      ctToCpeMap['datosVehiculo1.conductorDireccion1'] =
        CPEClaim?.personsInvolved[0]?.addresLine1;
      ctToCpeMap['datosVehiculo1.conductorCodigoPostal'] =
        CPEClaim?.personsInvolved[0]?.zipCode;
    }

    const value = ctToCpeMap[field] ?? '';

    return value;
  };

  useEffect(() => {
    setIsFinishDisableByCondition(false);
  }, [currentClaimForm?.info?.case]);

  useEffect(() => {
    const updateSessionStorage = () => {
      const currentValues = formikRef?.current?.values || {};
      const storedValues = JSON.parse(
        sessionStorage.getItem('formikValues') || '{}',
      );
      const updatedValues = { ...storedValues, ...currentValues };
      sessionStorage.setItem('formikValues', JSON.stringify(updatedValues));
    };

    const intervalId = setInterval(() => {
      if (formikRef?.current) {
        updateSessionStorage();
      }
    }, 400);

    return () => clearInterval(intervalId);
  }, [formikRef]);

  useEffect(() => {
    const storedValues = JSON.parse(
      sessionStorage.getItem('formikValues') || '{}',
    );

    const computedValues = sections.sections
      .map((item) => ({ ...item }))
      .map((item) => [...item.containers])
      .reduce((values, containers) => {
        containers.forEach((container) => {
          container.fields.forEach((field) => {
            let replace = field.modelBinding;
            if (field.modelBinding && field.modelBinding.includes('.')) {
              replace = field.modelBinding.replace('.', '&');
            }
            const fieldName = replace;

            // Get value from CPEClaim using our mapping function
            const cpeValue = field.modelBinding
              ? getValueFromCPEClaim(field.modelBinding, CPEClaim)
              : '';

            values[fieldName] =
              storedValues[fieldName] !== undefined &&
              storedValues[fieldName] !== '' &&
              storedValues[fieldName] !== 'ND'
                ? storedValues[fieldName]
                : field.defaultValue !== undefined && field.defaultValue !== ''
                ? field.defaultValue
                : cpeValue !== undefined && cpeValue !== ''
                ? cpeValue
                : '';
          });
        });

        return values;
      }, {});

    setInitialValues(computedValues);
    setValidationSchema(getValidationSchema(sections));

    const currentDetailId = sessionStorage.getItem('currentDetailId') ?? '';
    if (currentDetailId.length > 0 && currentDetailId !== claimUId) {
      sessionStorage.setItem('currentStep', '');
    } else {
      sessionStorage.setItem('currentStep', sections.stageName);
    }

    sessionStorage.setItem('currentDetailId', claimUId);

    if (formikRef?.current) {
      formikRef.current.setValues(computedValues);
    }
  }, [
    sections,
    formikRef,
    setInitialValues,
    setValidationSchema,
    claimUId,
    CPEClaim,
  ]);

  const triggerSave = useCallback(
    (
      data: any,
      requestNextStep: boolean | undefined,
      modelOptions?: OptionStep,
      reasonStatus?: string,
      textNotification?: string,
      newStatus?: NewStatus,
      sinister: boolean | undefined = true,
      approval: boolean | undefined = false,
    ) => {
      const keyValue =
        data && Object.keys(data).length > 0
          ? keyValueStructure(data)[0].split('::')
          : undefined;
      (async (): Promise<void> => {
        try {
          let hasErrorInSave = false;

          //Validate dataType
          if (keyValue) {
            const currentStep = sessionStorage.getItem('currentStep') ?? '';
            let currentSection = sections;
            if (currentStep !== sections?.stageName) {
              currentSection =
                currentClaimForm?.steps.find(
                  (s: any) => s.stageName === currentStep,
                ) ?? sections;
            }

            data[keyValue[0]] = transformFormValueByType(
              currentSection,
              keyValue[1],
              Object.keys(data)[0],
            );
          }

          // If the form has data to send
          if (Object.keys(data).length > 0) {
            FormDataFilter(data);
            const resultSave = await API.saveClaim(data, currentClaimForm);

            if (
              resultSave &&
              resultSave.data &&
              resultSave.data.eventRecord === currentClaimForm?.info.poNumber
            ) {
              hasErrorInSave = false;
              if (
                keyValue &&
                formikRef.current.values[keyValue[0]] !== keyValue[1]
              ) {
                formikRef.current.values[keyValue[0]] = keyValue[1];
              }
              setInitialValues(formikRef.current.values);

              if (currentClaimForm?.info.countryCode === CountryCode.PRI) {
                // Update vehicle mileage in CPE when odometer, Millaje or
                // datosVehiculo1.millaje is present
                const mileageRaw =
                  data.odometer != null && String(data.odometer).trim() !== ''
                    ? data.odometer
                    : data.Millaje != null && String(data.Millaje).trim() !== ''
                    ? data.Millaje
                    : data.datosVehiculo1?.millaje != null &&
                      String(data.datosVehiculo1?.millaje).trim() !== ''
                    ? data.datosVehiculo1?.millaje
                    : data.datosVehiculo2?.millaje != null &&
                      String(data.datosVehiculo2?.millaje).trim() !== ''
                    ? data.datosVehiculo2?.millaje
                    : null;

                if (cpeIds?.cpeClaimId && mileageRaw != null) {
                  const mileageNum = Number(
                    String(mileageRaw).replace(/[^0-9]/g, ''),
                  );
                  if (!Number.isNaN(mileageNum) && mileageNum >= 0) {
                    try {
                      await updateClaimVehicleMileageCPE(
                        cpeIds?.cpeClaimId,
                        mileageNum,
                      );
                    } catch (e) {
                      console.warn('Failed to update CPE vehicle mileage', e);
                    }
                  }
                } else {
                  console.warn(
                    'No mileage found to update CPE vehicle mileage, using odometer, Millaje or datosVehiculo1.millaje',
                    data,
                    cpeIds,
                  );
                }
              }

              if (
                currentClaimForm?.info?.countryCode === 'PRI' &&
                data.IndEstimate != null
              ) {
                window.location.reload();
              }
            } else {
              hasErrorInSave = true;
              setLoading(false);

              console.log('Error al procesar el guardado.');
              setIsDetectedError(true);
              setMsgError(JSON.stringify(resultSave));
            }
          }

          if (requestNextStep && !hasErrorInSave) {
            triggerStatus(
              {
                CurrentStage: currentClaimForm?.info.currentStep,
                CurrentStatus: currentClaimForm?.info.currentStepStatus,
                UserId: Number(localStorage.getItem('userId')),
                RequiredApproval: approval,
                ReasonStatusChange: reasonStatus,
                TextNotification: textNotification,
                NewStatus: newStatus,
                ...modelOptions,
              },
              sinister,
            );
          } else {
            dispatch({ type: ActionType.SET_LOADING, payload: false });
          }
        } catch (error) {
          setLoading(false);
          console.error('Error saving claim:', error);
          setIsDetectedError(true);
          setMsgError(JSON.stringify(error));
        } finally {
          setIsDisabledButtonConfirm(false);
        }
      })();
    },
    [],
  );

  const triggerStatus = useCallback((data, sinister: boolean | undefined) => {
    (async (): Promise<void> => {
      try {
        localStorage.setItem('currentDetailId', 'refresh');
        sessionStorage.setItem('currentStep', '');

        console.warn('ACTION STEP', currentClaimForm?.info.currentStepStatus);

        const resultAction = await actionStep(
          currentClaimForm?.info.currentStepStatus,
          sinister,
        );

        console.warn('RESULT_ACTION', resultAction);

        if (resultAction) {
          dispatch({ type: ActionType.SET_LOADING, payload: true });
          await API.putClaimStatus(data, claimUId, cpeIds?.cpeClaimId);
          history.go(0);
        }
      } catch (error) {
        console.error('Error updating claim status:', error);
      }
    })();
  }, []);

  const onChangeForm = (e: any) => {
    if (formikRef.current) {
      const { name, className, innerText } = e.target;

      let isDatePickerElement = false;
      let isSelectedElement = false;
      let isTimePickerElement = false;
      let isDamageSelection = false;

      if (className) {
        isDatePickerElement = className.includes('MuiPickersDay-root');
        isSelectedElement = className.includes('MuiMenuItem-root');
        isTimePickerElement =
          innerText !== undefined && innerText.includes('GUARDAR HORA');
        isDamageSelection = className.includes('damageCheck');
      }

      if (
        formikRef.current.dirty &&
        !isEqual(formikRef.current.values, formikRef.current.initialValues) &&
        ((name &&
          !name.includes('custom_') && // Custom fields from custom components are not validated
          String(name).length > 0) ||
          isDatePickerElement ||
          isSelectedElement ||
          isTimePickerElement ||
          isDamageSelection)
      ) {
        console.log('Procesando cambios...');
        handleSubmit(false);
      }
    }
  };

  const calculateDateDiff = (startDate, endDate): number => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    const diff = end.diff(start, 'second');

    return diff;
  };

  const onClickForm = (e: any) => {
    setTimeout(() => {
      if (formikRef.current) {
        const { name, className } = e.target;
        let idDamages = false;

        idDamages = String(className)?.includes('damagesModal__saveBtn');

        if (
          formikRef.current.dirty &&
          !isEqual(formikRef.current.values, formikRef.current.initialValues) &&
          ((name && !name.includes('custom_') && String(name).length > 0) ||
            idDamages)
        ) {
          console.log('Procesando cambios...');
          handleSubmit(false);
        }
      }
    }, 400);
  };

  const handleSubmit = (
    requestNextStep?: boolean | undefined,
    sinister: boolean | undefined = true,
  ) => {
    setIsDisabledButtonConfirm(true);
    const lastDate = new Date();
    const diff = calculateDateDiff(saveInProcess, lastDate);
    if (diff >= 2) {
      setSaveInProcess(lastDate);
      const modifiedFields: any = {};
      const updatedData: any = {};
      setShowConfirmSubmit(false);

      if (formikRef.current) {
        const values = formikRef.current.values;
        Object.keys(values).forEach((fieldName) => {
          if (values[fieldName] !== initialValues[fieldName]) {
            modifiedFields[fieldName] = values[fieldName];
          }
        });

        Object.keys(modifiedFields).forEach((key) => {
          if (key.includes('&')) {
            const updatedKey = key.replace('&', '.');
            set(updatedData, updatedKey, modifiedFields[key]);
          } else {
            set(updatedData, key, modifiedFields[key]);
          }
        });

        if (
          legacyCurrentClaim?.jsonFieldData?.event.CompanyId !==
            BrandsId.PURDY &&
          !optionsStep.ChangeStatusRevert &&
          currentClaimForm?.info.countryCode !== CountryCode.COL &&
          sections.stageName === 'DAMAGE_ANALYSIS' &&
          ((currentClaimForm?.info.currentStepStatus ===
            StepStatus.PENDING_ESTIMATE &&
            !state.estimatedAmount &&
            legacyCurrentClaim?.jsonFieldData?.event.CompanyId !==
              BrandsId.CSM) ||
            (currentClaimForm?.info.currentStepStatus ===
              StepStatus.PENDING_ADJUSTMENT &&
              !state.adjustAmount))
        ) {
          setOpenDialogMsg(
            `Para continuar con el proceso, es necesario que realices al menos un ${CurrentStatusText[
              currentClaimForm?.info.currentStepStatus
            ].toLowerCase()}.`,
          );

          setOpenDialog(true);
          setIsDisabledButtonConfirm(false);

          return;
        }

        if (
          currentClaimForm?.info.currentStepStatus ===
          StepStatus.PENDING_APPRO_AGREE
        ) {
          const agreementType = localStorage.getItem('agreementType') ?? '';
          if (
            !optionsStep.ChangeStatusRevert &&
            currentClaimForm?.info.countryCode !== CountryCode.COL &&
            Number(legacyCurrentClaim?.jsonFieldData?.event?.CompanyId) !==
              BrandsId.PURDY &&
            agreementType.length === 0
          ) {
            setOpenDialogMsg(
              'Para continuar con el proceso, es necesario tener los parámetros del acuerdo para el pago / reparación.',
            );

            setOpenDialog(true);
            setIsDisabledButtonConfirm(false);

            return;
          }
        }

        if (
          !optionsStep.ChangeStatusRevert &&
          sections.stageName === 'OPTIONS_AGREEMENT' &&
          currentClaimForm?.info.currentStepStatus ===
            StepStatus.PENDING_APPRO_AGREE &&
          legacyCurrentClaim?.requiresApproval &&
          state.adjustAmount &&
          !legacyCurrentClaim?.closedWithoutPayment &&
          !optionsStep.IsClaimNotApplicable
        ) {
          requestApproval();
        }

        if (requestNextStep && optionsStep.ChangeStatusRevert) {
          if (!statusSelected) {
            setOpenDialogMsg(
              'Para continuar con el proceso, seleccione un estado.',
            );

            setOpenDialog(true);
            setIsDisabledButtonConfirm(false);

            return;
          }
        }

        if (
          requestNextStep &&
          (optionsStep.IsClaimNotApplicable || optionsStep.Objeted)
        ) {
          if (!reasonStatusChange || reasonStatusChange?.length < 5) {
            setOpenDialogMsg(
              'Para continuar con el proceso, es necesario agregar una justificación que indique porqué el caso no procede.',
            );

            setOpenDialog(true);
            setIsDisabledButtonConfirm(false);

            return;
          }

          if (optionsStep.Objeted && !statusSelected) {
            setOpenDialogMsg(
              'Para continuar con el proceso, seleccione un estado.',
            );

            setOpenDialog(true);
            setIsDisabledButtonConfirm(false);

            return;
          }
        }

        if (requestNextStep && optionsStep.RequireInformation) {
          if (!textNotification || textNotification?.length === 0) {
            setOpenDialogMsg(
              'Para continuar con el proceso, es necesario seleccionar al menos una razón que indique la información pendiente.',
            );

            setOpenDialog(true);
            setIsDisabledButtonConfirm(false);

            return;
          }

          if (
            (currentClaimForm?.info.countryCode === CountryCode.PAN ||
              currentClaimForm?.info.countryCode === CountryCode.PRI) &&
            !reasonSelect
          ) {
            setOpenDialogMsg(
              'Para continuar con el proceso, es necesario agregar un motivo que indique la información pendiente.',
            );

            setOpenDialog(true);
            setIsDisabledButtonConfirm(false);

            return;
          } else {
            dispatch({ type: ActionType.SET_LOADING, payload: true });
            triggerSave(
              updatedData,
              requestNextStep,
              optionsStep,
              reasonStatusChange,
              currentClaimForm?.info.countryCode === CountryCode.PAN ||
                currentClaimForm?.info.countryCode === CountryCode.PRI
                ? reasonSelect + ' :: ' + textNotification
                : textNotification,
              statusSelected,
              sinister,
              legacyCurrentClaim?.requiresApproval,
            );

            return;
          }
        }

        const executeSubmissionWithSideEffect = async () => {
          try {
            if (
              requestNextStep &&
              pendingSideEffect.sideEffect &&
              pendingSideEffect.claimId
            ) {
              try {
                await pendingSideEffect.sideEffect(
                  pendingSideEffect.claimId,
                  pendingSideEffect.context,
                );
                setPendingSideEffect({ sideEffect: null, claimId: null });
              } catch (error) {
                console.error('Error executing side effect:', error);
              }
            }

            triggerSave(
              updatedData,
              requestNextStep,
              optionsStep,
              reasonStatusChange,
              textNotification,
              statusSelected,
              sinister,
              legacyCurrentClaim?.requiresApproval,
            );
          } catch (error) {
            console.error('Error in submission process:', error);
            setIsDisabledButtonConfirm(false);
          }
        };

        dispatch({ type: ActionType.SET_LOADING, payload: true });
        executeSubmissionWithSideEffect();
      }
    }
  };

  const actionStep = async (
    stepStatus: StepStatus,
    sinister: boolean | undefined,
  ) => {
    switch (stepStatus) {
      case StepStatus.PENDING_SINISTER: {
        if (!sinister) {
          return true;
        }
        const legacy = await getClaimById(claimUId);
        switch (legacy?.jsonFieldData?.event?.CompanyId) {
          case BrandsId.QUALITAS: {
            try {
              await sinisterClaims(legacy?.jsonFieldData, dispatch);

              return true;
            } catch (error) {
              console.warn('Sinister Error:', error);

              return false;
            }
          }
          default:
            return true;
        }
      }
      case StepStatus.PENDING_INSPECT: {
        const legacy = await getClaimById(claimUId);

        if (legacy.jsonFieldData) {
          await finishEvent(legacy?.jsonFieldData);
        }

        return true;
      }
      default:
        return true;
    }
  };

  const requestApproval = async (): Promise<any> => {
    try {
      await new ClaimsTrackerClient().requestApproval(
        claimUId,
        'FINAL_AGREEMENT',
      );
    } catch (e) {
      console.error(e);
    }
  };
  const [visibleDepreciation, setVisibleDepreciation] = useState<boolean>(
    false,
  );

  const targetSection = (
    type: string,
    item: any,
    errors: any,
    touched: any,
    setErrors?: (errors: { [key: string]: any }) => void,
  ) => {
    switch (type) {
      case FieldTypes.PHOTOS:
        return (
          <Photos
            eventRecord={currentClaimForm?.info?.poNumber}
            claimantType={
              String(currentClaimForm?.info?.type).toUpperCase() as ClaimantType
            }
            inspectionFlags={inspectionFlags}
            setInspectionFlags={setInspectionFlags}
          />
        );
      case FieldTypes.ESTIMATED_PAYMENT:
        return (
          <>
            <ClaimEstimated
              claimForm={currentClaimForm}
              claimUId={claimUId}
              claimNumber={currentClaimForm?.info?.case}
              poNumber={currentClaimForm?.info?.poNumber}
              claimantType={currentClaimForm?.info?.type}
              disabled={isDisabled}
              accountReference={
                Number(legacyCurrentClaim?.accountReference) || 0
              }
              policyNumber={legacyCurrentClaim?.owner?.policyNumber}
              CPEClaim={CPEClaim}
              cpeIds={cpeIds}
            />
          </>
        );
      case FieldTypes.EXPENSES:
        return (
          <ClaimExpenses
            claimNumber={currentClaimForm?.info?.case}
            poNumber={currentClaimForm?.info?.poNumber}
            claimUId={claimUId}
            claimantType={currentClaimForm?.info?.type}
            disabled={isDisabled}
            cpeIds={cpeIds}
          />
        );
      case FieldTypes.REQUEST_CLAIMS:
        return (
          <ClaimRequest
            currentClaim={legacyCurrentClaim}
            onLoad={(value: boolean) => {
              dispatch({ type: ActionType.SET_LOADING, payload: value });
            }}
            disabled={isDisabled}
            onRefresh={() => {
              (async (): Promise<void> => {
                try {
                  const searchClaim = await getClaimById(claimUId);
                  if (setLegacyCurrentClaim) {
                    setLegacyCurrentClaim(searchClaim);
                  }
                  dispatch({ type: ActionType.SET_LOADING, payload: false });
                } catch (e) {
                  console.error(e);
                  dispatch({ type: ActionType.SET_LOADING, payload: false });
                }
              })();
            }}
            onNotify={(message: string, isWarn = false) => {
              dispatch({
                type: ActionType.SET_GENERAL_ALERT,
                payload: {
                  showAlert: true,
                  alertMessage: message,
                  alertType: isWarn ? 'warning' : 'success',
                },
              });
            }}
            cpeIds={cpeIds}
          />
        );
      case FieldTypes.AGREEMENT_DETAIL: {
        const hasPreviousPayment =
          legacyCurrentClaim?.previousPayment === true &&
          (currentClaimForm?.info?.currentStepStatus ===
            StepStatus.PENDING_PAYMENT ||
            currentClaimForm?.info?.currentStepStatus ===
              StepStatus.PENDING_APPRO_AGREE);

        return (
          <>
            {hasPreviousPayment && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>Pago procesado</AlertTitle>
                Esta reclamación ya cuenta con un pago procesado.
              </Alert>
            )}
            <AgreementDetail
              claimUId={claimUId}
              claimForm={currentClaimForm}
              disabled={isDisabled}
              legacyCurrentClaim={legacyCurrentClaim}
              disableFinishActionState={setIsFinishDisableByCondition}
            />
          </>
        );
      }
      case FieldTypes.TOTAL_LOSS:
        return (
          <TotalLoss
            claimUId={claimUId}
            claimForm={currentClaimForm}
            disabled={isDisabled}
            visibleDepreciation={setVisibleDepreciation}
            cpeIds={cpeIds}
          />
        );
      case FieldTypes.RESERVE:
        return (
          <Reserve
            claimUId={claimUId}
            claimForm={currentClaimForm}
            legacyCurrentClaim={legacyCurrentClaim}
          />
        );
      case FieldTypes.SUBROGATION:
        return (
          <Subrogation
            claimUId={claimUId}
            claimForm={currentClaimForm}
            notifySubrogation={legacyCurrentClaim?.notifySubrogation}
            disabled={isDisabled}
            cpeIds={cpeIds}
          />
        );
      case FieldTypes.EVENT_TRACKING:
        return (
          <EventTracking claimUId={claimUId} claimForm={currentClaimForm} />
        );
      case FieldTypes.FINAL_RESOLUTION:
        return (
          <FinalResolution
            claimUId={claimUId}
            claimForm={currentClaimForm}
            disabled={isDisabled}
          />
        );
      case FieldTypes.DEPRECIATION:
        return (
          <>
            {visibleDepreciation ? (
              <Depreciation
                claimUId={claimUId}
                claimNumber={currentClaimForm?.info?.case}
                poNumber={currentClaimForm?.info?.poNumber}
                totalIsured={currentClaimForm?.info?.policy.totalInsured}
                claimantType={currentClaimForm?.info?.type}
              />
            ) : null}
          </>
        );
      case FieldTypes.THIRDS_CLAIMS:
        return (
          <ThirdsForm
            claim={currentClaimForm}
            isInspectorForm={false}
            type={FieldTypes.THIRDS_CLAIMS}
          />
        );
      case FieldTypes.INJURED_CLAIMS:
        return (
          <ThirdsForm
            claim={currentClaimForm}
            isInspectorForm={false}
            type={FieldTypes.INJURED_CLAIMS}
          />
        );
      case FieldTypes.AUTO_PARTS:
        return <ClaimAutoParts claimUId={claimUId} disabled={false} />;
      case FieldTypes.CIRCUNSTANCES_CLAIMS:
        return (
          <CircunstancesFormClaims
            circunstancesList={CATALOGS.circunstances}
            thirdPlate={
              legacyCurrentClaim?.jsonFieldData?.event.plateCircunstancesThird
            }
            thirdInsurance={
              legacyCurrentClaim?.jsonFieldData?.event
                .insuranceCircunstancesThird
            }
            isActive={Number(localStorage.getItem('rolId')) !== Rol.INSURER}
            codeVehicleA={
              legacyCurrentClaim?.jsonFieldData?.event.insuredCodeConsequences
            }
            codeVehicleB={
              legacyCurrentClaim?.jsonFieldData?.event.thirdCodeConsequences
            }
            responsable={
              legacyCurrentClaim?.jsonFieldData?.event.responsableConsequences
            }
            save={(data: any) => {
              triggerSave(data, false);
            }}
          />
        );
      case FieldTypes.PURDY_ESTIMATED_PAYMENT:
        localStorage.setItem('current_estimation_id', '');

        return (
          <PurdyEstimationComponent
            claimUId={claimUId}
            poNumber={currentClaimForm?.info?.poNumber}
            currentClaimForm={currentClaimForm}
          />
        );
        return <>Purdy Estimate Process</>;
      case FieldTypes.PURDY_SINISTER_PROCESS:
        //return <PurdySinisterProcess claimUId={claimUId} disabled={false} />;
        return <>Purdy Sinister Process</>;
      default: {
        return (
          <FormFieldsComponent
            section={item}
            claim={currentClaimForm?.info}
            event={legacyCurrentClaim?.jsonFieldData}
            errors={errors}
            touched={touched}
            disabled={isDisabled}
            disableFinishActionState={setIsFinishDisableByCondition}
            setErrors={setErrors}
          />
        );
      }
    }
  };

  const handleGeneratePDF = (): void => {
    setLoading(true);
    (async (): Promise<void> => {
      await API.postGeneratePDFs(currentClaimForm?.info.poNumber);
      setLoading(false);
    })();
  };

  const download = async () => {
    // La descarga del evento se hará depenindo de si es un estado mayor a 2
    if (legacyCurrentClaim?.eventRecord) {
      downloadEvent(
        currentClaimForm?.info.poNumber,
        currentClaimForm?.info.countryCode,
        Number(localStorage.getItem('rolId')),
        dispatch,
      );
    }
  };

  const sendPdf = async () => {
    const eventNumber = legacyCurrentClaim?.eventRecord ?? '';
    const response = await sendClientPdf(eventNumber);

    const message =
      response === ''
        ? 'El Pdf fue enviado con éxito.'
        : 'Ocurrió un error al enviar el Pdf.';
    dispatch({
      type: ActionType.SET_GENERAL_ALERT,
      payload: {
        showAlert: true,
        alertMessage: message,
        alertType: response === '' ? 'success' : 'error',
      },
    });
  };

  const triggerActiveLink = async () => {
    try {
      setLoading(true);
      const response = await sendActiveLink(legacyCurrentClaim?.eventId);
      setLoading(false);
      const message = response
        ? '<b>Solicitud enviada satisfactoriamente. Ver más detalles en la bitácora del evento.</b>'
        : '<b>No se puede enviar la solicitud. Reintente nuevamente.</b>';
      dispatch({
        type: ActionType.SET_GENERAL_ALERT,
        payload: {
          showAlert: true,
          alertMessage: message,
          alertType: response ? 'success' : 'error',
        },
      });
    } catch (e) {
      setLoading(false);
      console.error(e);
    }

    setConfirmActiveLink(false);
  };

  const checkFieldDependencySection = (dependencyJson: any): boolean => {
    const dependencyFields = JSON.parse(dependencyJson);

    let _showSection = true;

    if (dependencyFields) {
      for (const dependency of dependencyFields) {
        const _containers = steps
          .map((item) =>
            item.sections.find(
              (section) => section.id === dependency.SectionId,
            ),
          )
          .find((section) => section !== undefined)?.containers;

        const _section = _containers ? _containers[0] : null;

        //validacion de dependencias de campos que estan dentro de componentes custom
        if (!_section) {
          switch (dependency.Name) {
            case 'thirdType': {
              const value = state?.currentInspection?.thirds
                ? (state?.currentInspection?.thirds[0] as any)?.thirdType
                : undefined;

              if (value && value !== dependency.ExpectedValue) {
                return false;
              }
            }
          }
        }

        if (_section) {
          const _field = _section.fields?.find(
            (f: any) => f.keyName === dependency.Name,
          );

          const values = JSON.parse(
            sessionStorage.getItem('formikValues') || '{}',
          );

          if (_field && values) {
            const cpeValue = _field.modelBinding
              ? getValueFromCPEClaim(_field.modelBinding, CPEClaim)
              : undefined;

            const value =
              values[_field.modelBinding] !== undefined &&
              values[_field.modelBinding] !== ''
                ? values[_field.modelBinding]
                : cpeValue !== undefined
                ? cpeValue
                : _field.defaultValue;

            if (value && value !== dependency.ExpectedValue) {
              _showSection = false;
              break;
            }
          }
        }
      }
    }

    return _showSection;
  };

  const detectStatusForFinalizeButton = (): void => {
    let detectStatus = isDisabledButton; // Detect is disable by some conditions (see isDisabledButton variable)

    detectStatus = !detectStatus ? !enabledByStates : detectStatus; // Detect is disable by the Current Status
    detectStatus = !detectStatus ? isFinishDisableByCondition : detectStatus; // Detect is disable by condition
    detectStatus = !detectStatus ? pendingApproval : detectStatus; // Detect is disable by approval
    detectStatus = !detectStatus
      ? sections.stageName !== currentClaimForm?.info?.currentStep
      : detectStatus; // Detect current step is distinct selected step

    setFinishBtnIsDisable(detectStatus);
  };

  const validateStatusByAttributes = (): void => {
    if (
      legacyCurrentClaim &&
      legacyCurrentClaim?.jsonFieldData &&
      currentClaimForm
    ) {
      const fieldData = legacyCurrentClaim?.jsonFieldData;
      const { info } = currentClaimForm;

      if (
        BrandsId.CSM === (fieldData?.event.CompanyId ?? 0) &&
        fieldData?.event.IndEstimate === '2' &&
        info?.currentStep === 'INSPECT_SECTION' &&
        info?.currentStepStatus === 'PENDING_AUDIT'
      ) {
        setShowFinishWithoutEstimatedButton(
          userRole === Rol.ADJUSTER ||
            userRole === Rol.ADMIN ||
            userRole === Rol.AUDIT,
        );
      }

      if (
        BrandsId.CSM === (fieldData?.event.CompanyId ?? 0) &&
        fieldData?.event.IndEstimate === '1' &&
        info?.currentStep === 'INSPECT_SECTION' &&
        info?.currentStepStatus === 'PENDING_AUDIT'
      ) {
        setShowFinishWithoutEstimatedButton(false);
      }
    }
  };

  useEffect(() => {
    setTimeout(() => {
      detectStatusForFinalizeButton();
      setIsFirstRender(false);
    }, 2500);
  }, [state.claimsManagementInfo]);

  useEffect(() => {
    if (!isFirstRender) {
      setIsFinishDisableByCondition(false);
      // Detect status
      detectStatusForFinalizeButton();
    }
  }, [sections]);

  useEffect(() => {
    validateStatusByAttributes();
  }, [legacyCurrentClaim]);

  const handleStageButtonClick = async () => {
    const claimId = cpeIds?.cpeClaimId;
    if (claimId) {
      try {
        const currentStage = sections.submitTitle;
        const handler = stageHandlers[currentStage];

        if (handler) {
          await handler.validate(claimId);

          // Store the side effect for later execution if it exists
          if (handler.sideEffects) {
            setPendingSideEffect({
              sideEffect: handler.sideEffects,
              claimId,
              context: { cpeInspectionId: CPEClaim?.inspection?.id },
            });
          } else {
            setPendingSideEffect({ sideEffect: null, claimId: null });
          }
        } else {
          setPendingSideEffect({ sideEffect: null, claimId: null });
        }

        runDefaultBehavior({
          initOptionStep,
          setReasonStatusChange,
          setTextNotification,
          setStatusSelected,
          setIsDisabledButtonConfirm,
          setShowConfirmSubmit,
        });
      } catch (error: any) {
        console.error(error.message);
        setOpenDialogMsg(error.message);
        setOpenDialog(true);
      }
    } else {
      runDefaultBehavior({
        initOptionStep,
        setReasonStatusChange,
        setTextNotification,
        setStatusSelected,
        setIsDisabledButtonConfirm,
        setShowConfirmSubmit,
      });
    }
  };

  const letterStyling = {
    mainModal: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '90vw',
      height: '90vh',
      bgcolor: 'background.paper',
      boxShadow: 24,
      borderRadius: '12px',
      p: 4,
      overflowY: 'scroll',
      scrollbarGutter: 'stable',
    },
    loadingModal: {
      position: 'absolute' as const,
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '30vw',
      height: '30vh',
      bgcolor: 'background.paper',
      boxShadow: 24,
      borderRadius: '12px',
      p: 4,
    },
  };

  const SentLettersList: React.FC<SentLetters> = ({ sentLetters }) => {
    const handlePreview = async (letter: SentLetter) => {
      // Loading modal
      setIsSentLetterPreviewClicked(true);

      // Generate PDF
      try {
        const pdfBlob = await postDownloadSentLetter(letter.key);

        if (pdfBlob?.status === 200) {
          console.log(logString, 'Previewed sent letter');
          unsuccessfulRequest.current = null;
          setDidRequestFail(false);
          // Preview PDF in a new window
          const url = URL.createObjectURL(pdfBlob.data);
          window.open(url, '_blank');
          setIsSentLetterPreviewClicked(false);
          setTimeout(() => URL.revokeObjectURL(url), 1000 * 60);
        } else {
          console.warn(
            logString,
            'Could not preview sent letter: ',
            pdfBlob?.data,
          );
          unsuccessfulRequest.current = 'postDownloadSentLetter_Preview';
          setDidRequestFail(true);
        }
      } catch (e) {
        console.error(logString, 'Error previewing sent letter: ', e);
        setRequestError(e);
        setRequestResponse(
          (e as AxiosError).response ? (e as AxiosError).response : undefined,
        );
        unsuccessfulRequest.current = 'postDownloadSentLetter_Preview';
        setDidRequestThrow(true);
      }
    };

    const handleDownload = async (letter: SentLetter) => {
      // Download modal
      setIsSentLetterDownloadClicked(true);
      setIsSentLetterDownloading(true);

      // Generate PDF
      try {
        const pdfBlob = await postDownloadSentLetter(letter.key);

        if (pdfBlob?.status === 200) {
          console.log(logString, 'Downloaded sent letter');
          unsuccessfulRequest.current = null;
          setDidRequestFail(false);
          // Download PDF
          const blobUrl = URL.createObjectURL(pdfBlob.data);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = letter.fileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(blobUrl);
        } else {
          console.warn(
            logString,
            'Could not download sent letter: ',
            pdfBlob?.data,
          );
          unsuccessfulRequest.current = 'postDownloadSentLetter_Download';
          setDidRequestFail(true);
        }
        // Reset
        setIsSentLetterDownloading(false);
      } catch (e) {
        console.error(logString, 'Error downloading sent letter: ', e);
        setRequestError(e);
        setRequestResponse(
          (e as AxiosError).response ? (e as AxiosError).response : undefined,
        );
        unsuccessfulRequest.current = 'postDownloadSentLetter_Download';
        setDidRequestThrow(true);
      }
    };

    return (
      <Box display="flex" flexDirection="column" gap={3}>
        {sentLetters.map((letter, index) => (
          <Box
            key={index}
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            border="1px solid #ccc"
            borderRadius="8px"
            p={2}
          >
            {/* Left Column */}
            <Box>
              <Typography>
                <strong>Nombre del archivo:</strong> {letter.fileName}
              </Typography>
              <Typography>
                <strong>Tipo de plantilla:</strong> {letter.templateType}
              </Typography>
              <Typography>
                <strong>Enviado a:</strong> {letter.recipientEmail}
              </Typography>
              <Typography>
                <strong>Fecha:</strong>{' '}
                {new Date(letter.lastModified).toLocaleDateString()}
              </Typography>
            </Box>

            {/* Right Column - Actions */}
            <Box
              display="flex"
              flexDirection="column"
              alignItems="flex-end"
              gap={1}
            >
              <Button
                variant="outlined"
                size="small"
                onClick={() => handlePreview(letter)}
                text="Ver documento"
              ></Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => handleDownload(letter)}
                text="Descargar"
              ></Button>
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const LetterReviewScreen: React.FC = () => {
    const [sendSelection, setSendSelection] = useState<string>(
      requiresPrevious ? previousSendSelection : 'insuredPerson',
    );
    const [personToSendLetter, setPersonToSendLetter] = useState<Recipient>();
    const [personType, setPersonType] = useState<string>(
      requiresPrevious ? previousPersonType : '(asegurado)',
    );

    useEffect(() => {
      if (sendSelection === 'insuredPerson') {
        setPersonToSendLetter(
          prefilledLetter?.recipients.insuredPerson ?? undefined,
        );
        setPersonType('(asegurado)');
        setPreviousPersonType('(asegurado)');
      } else if (sendSelection === 'driver') {
        setPersonType('(conductor)');
        setPreviousPersonType('(conductor)');
        setPersonToSendLetter(prefilledLetter?.recipients.driver ?? undefined);
      }
    }, [sendSelection]);

    const [showSendConfirmation, setShowSendConfirmation] = useState(false);

    const handleSubmit = async () => {
      if (templateSubmitAction.current === 'download') {
        // Download modal
        setIsTemplateLetterDownloadClicked(true);
        setIsTemplateLetterDownloading(true);

        try {
          // Generate PDF
          const pdfBlob = await postDownloadModifiedLetterTemplate(
            selectedLetterTemplate?.id ?? '',
            claimIdForLetter,
            personToSendLetter?.id ?? '',
          );

          if (pdfBlob?.status === 200) {
            console.log(logString, 'Downloaded modified letter');
            unsuccessfulRequest.current = null;
            setDidRequestFail(false);
            // Download PDF
            const blobUrl = URL.createObjectURL(pdfBlob.data);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${
              CPEClaim?.claim?.poNumber ? CPEClaim.claim.poNumber + '_' : ''
            }${selectedLetterTemplate?.spanishName}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
          } else {
            console.warn(
              logString,
              'Could not download modified letter: ',
              pdfBlob?.data,
            );
            unsuccessfulRequest.current =
              'postDownloadModifiedLetterTemplate_Download';
            setDidRequestFail(true);
          }
          // Reset
          templateSubmitAction.current = null;
          setIsTemplateLetterDownloading(false);
        } catch (e) {
          console.error(logString, 'Error downloading modified letter: ', e);
          setRequestError(e);
          setRequestResponse(
            (e as AxiosError).response ? (e as AxiosError).response : undefined,
          );
          unsuccessfulRequest.current =
            'postDownloadModifiedLetterTemplate_Download';
          setDidRequestThrow(true);
        }
      } else if (templateSubmitAction.current === 'send') {
        // Send modal
        setIsTemplateLetterSendClicked(true);
        setIsTemplateLetterSending(true);

        try {
          // Send email
          const response = await postSendModifiedLetterTemplate(
            selectedLetterTemplate?.id ?? '',
            claimIdForLetter,
            personToSendLetter?.id ?? '',
          );

          if (response.status === 200) {
            console.log(logString, 'Sent modified letter');
            setRefreshSentLetters(true);
          } else {
            console.warn(
              logString,
              'Could not send modified letter: ',
              response?.data,
            );
            unsuccessfulRequest.current =
              'postDownloadModifiedLetterTemplate_Send';
            setDidRequestFail(true);
          }
          // Reset
          templateSubmitAction.current = null;
          setIsTemplateLetterSending(false);
        } catch (e) {
          console.error(logString, 'Error sending modified letter: ', e);
          setRequestError(e);
          setRequestResponse(
            (e as AxiosError).response ? (e as AxiosError).response : undefined,
          );
          unsuccessfulRequest.current =
            'postDownloadModifiedLetterTemplate_Send';
          setDidRequestThrow(true);
        }
      }
    };

    return (
      <>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
        >
          <FormControl>
            <InputLabel id="person-select-label">Carta para...</InputLabel>
            <Select
              label="Carta para..."
              value={sendSelection}
              onChange={(e) => {
                setSendSelection(e.target.value);
                setPreviousSendSelection(e.target.value);
              }}
            >
              {prefilledLetter?.recipients?.insuredPerson && (
                <MenuItem value="insuredPerson">{`${prefilledLetter.recipients.insuredPerson.name} (asegurado)`}</MenuItem>
              )}
              {prefilledLetter?.recipients?.driver && (
                <MenuItem value="driver">{`${prefilledLetter.recipients.driver.name} (conductor)`}</MenuItem>
              )}
            </Select>
          </FormControl>
          <TextField
            key={personToSendLetter?.email ?? 'send-email'}
            label={'email'}
            value={personToSendLetter?.email ?? 'n/a'}
            variant="outlined"
            disabled
          />

          {Array.from({ length: 14 }).map((_, idx) => (
            <br key={idx}></br>
          ))}

          <Box sx={{ display: 'flex', gap: 3, mt: 2 }}>
            <Button
              style={{ flex: 2 }}
              variant="outlined"
              type="submit"
              text="Descargar"
              onClick={() => (templateSubmitAction.current = 'download')}
            ></Button>
            {selectedLetterTemplate?.canSend && (
              <Button
                style={{ flex: 1 }}
                variant="contained"
                text="Enviar"
                onClick={() => setShowSendConfirmation(true)}
              ></Button>
            )}
          </Box>
        </Box>
        <Modal open={showSendConfirmation}>
          <Box sx={letterStyling.loadingModal}>
            <div style={{ height: '100%' }}>
              <Box
                sx={letterStyling.loadingModal}
                display="flex"
                flexDirection="column"
                justifyContent="left"
                alignItems="left"
                gap={2}
                height="100%"
              >
                {personToSendLetter && (
                  <Typography variant="h6" align="center">
                    Esta carta será enviada a:
                  </Typography>
                )}
                {personToSendLetter?.name && (
                  <Typography>
                    <strong>Nombre:</strong>{' '}
                    {personToSendLetter.name + ' ' + personType}
                  </Typography>
                )}
                {personToSendLetter?.email && (
                  <Typography>
                    <strong>Correo electrónico:</strong>{' '}
                    {personToSendLetter.email}
                  </Typography>
                )}
                {!personToSendLetter && (
                  <Typography variant="h6" align="center">
                    En estos momento no se puede enviar esta carta.
                  </Typography>
                )}
                <Box mt={3} display="flex" justifyContent="center" gap={2}>
                  <Button
                    variant="outlined"
                    text="Cancelar"
                    onClick={() => setShowSendConfirmation(false)}
                  ></Button>
                  {personToSendLetter && (
                    <Button
                      variant="contained"
                      text="Confirmar"
                      onClick={() => {
                        templateSubmitAction.current = 'send';
                        setShowSendConfirmation(false);
                        handleSubmit();
                      }}
                    ></Button>
                  )}
                </Box>
              </Box>
            </div>
          </Box>
        </Modal>
      </>
    );
  };

  const RequestErrorAlert: React.FC<RequestErrorAlertProp> = ({ error }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      console.log(logString, 'Copied error into clipboard');
      navigator.clipboard.writeText(error).then(() => {
        setCopied(true);
      });
    };

    return (
      <>
        <Alert
          severity="error"
          sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          action={
            <Tooltip title="Copiar error">
              <IconButton
                size="small"
                aria-label="copy error text"
                onClick={handleCopy}
              >
                <ContentCopyIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          }
        >
          <Box>
            <AlertTitle>Error</AlertTitle>
            {error ? (
              <>
                Detalles del error:
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: 'rgba(0,0,0,0.04)',
                    p: 1,
                    borderRadius: 1,
                    mt: 1,
                    fontFamily: 'Monospace',
                    fontSize: '0.875rem',
                    whiteSpace: 'pre-wrap',
                  }}
                >
                  {error}
                </Box>
              </>
            ) : (
              <></>
            )}
          </Box>
        </Alert>

        <Snackbar
          open={copied}
          autoHideDuration={2000}
          onClose={() => setCopied(false)}
          message="Copiado al portapapeles"
        />
      </>
    );
  };

  return (
    <div className={isDetectedChanges ? 'fix-height-detail' : ''}>
      <Loader isActive={loading} />
      {isDetectedError && (
        <>
          <br />
          <Stack sx={{ width: '100%' }} spacing={2} className="alertError">
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setIsDetectedError(false);
                  }}
                  sx={{ mr: 1 }}
                >
                  X
                </IconButton>
              }
              sx={{ mb: 2 }}
            >
              <AlertTitle>
                <strong>¡Hemos tenido un problema!</strong>
              </AlertTitle>
              <p>
                No hemos podido procesar la acción solicitada. Por favor intente
                nuevamente, si el problema persiste comunicarse con soporte
                técnico y compartir la información del detalle.
              </p>
              <Accordion sx={{ backgroundColor: '#FDEDED' }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Typography>Detalle</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <code>{msgError}</code>
                </AccordionDetails>
              </Accordion>
            </Alert>
          </Stack>
        </>
      )}

      {sections && (
        <Box className="claim-sections">
          <Formik
            enableReinitialize={true}
            innerRef={formikRef}
            initialValues={initialValues}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={() => {
              console.warn('On submit');
            }}
            validateOnMount={true}
          >
            {({ errors, touched, isValid, setErrors }) => {
              sections.isInvalid = !isValid;

              return (
                <Form onBlur={onChangeForm} onClick={onClickForm}>
                  <Box>
                    <Grid
                      container
                      spacing={{ xs: 2, md: 3 }}
                      columns={{ xs: 4, sm: 8, md: 12 }}
                    >
                      <Grid item xs={12} sm={12} md={6}>
                        <p className="claim-sections__header--title">
                          {sections.title}
                        </p>
                        <p className="claim-sections__header--subtitle">
                          Fecha de {sections.title}:{' '}
                          {formatDate(
                            sections.title === 'Subrogación'
                              ? currentClaimForm?.info?.subrogation
                                  .SubrogationCreateDate
                              : sections.initialDate,
                            currentClaimForm?.info.countryCode,
                          )}
                        </p>
                        {sections.title === 'Subrogación' && (
                          <Grid container>
                            <Grid item>
                              <p
                                style={{
                                  color: '#F15B2B',
                                  backgroundColor: '#FFF2EE',
                                  padding: 8,
                                  margin: 10,
                                }}
                              >
                                ASEGURADORA:{' '}
                                {currentClaimForm?.info?.subrogation.insurance}
                              </p>
                            </Grid>
                            <Grid item>
                              <p
                                style={{
                                  color: '#001D3D',
                                  backgroundColor: '#EEF7FF',
                                  padding: 8,
                                  margin: 10,
                                }}
                              >
                                AJUSTADOR:{' '}
                                {currentClaimForm?.info?.subrogation.user}
                              </p>
                            </Grid>
                          </Grid>
                        )}
                      </Grid>

                      {userRole !==
                        roles.roles.filter(
                          (x) => x.name === 'auxiliar de reclamaciones',
                        )[0].id && (
                        <Grid item xs={12} sm={12} md={6}>
                          <Box sx={{ float: 'right' }}>
                            {showButtonChangeStatus && (
                              <>
                                <Button
                                  id="ChangeStatusRevert"
                                  text={'Cambio Estado'}
                                  onClick={handleClickOptionStep}
                                  style={customStyle}
                                />
                                |
                              </>
                            )}

                            {showFinishWithoutSinister && (
                              <>
                                <Button
                                  text={'Finalizar sin Siniestrar'}
                                  onClick={() => {
                                    handleSubmit(true, false);
                                  }}
                                  style={customStyle}
                                  variant="outlined"
                                  className="claim-button__outline-orange"
                                />
                                |
                              </>
                            )}

                            {showFinishWithoutEstimatedButton && (
                              <>
                                <Button
                                  id="IsFinishedWithoutEstimated"
                                  text={'Finalizar auditoría y finalizar'}
                                  onClick={handleClickOptionStep}
                                  style={customStyle}
                                  variant="outlined"
                                  className="claim-button__outline-orange"
                                />
                                |
                              </>
                            )}

                            {[
                              CountryCode.PAN,
                              CountryCode.CRC,
                              CountryCode.PRI,
                            ].includes(currentClaimForm?.info.countryCode) && (
                              <>
                                <Button
                                  key="btnDownloadButton"
                                  color="secondary"
                                  text="Descargar"
                                  onClick={() => download()}
                                ></Button>
                                |
                              </>
                            )}

                            {[CountryCode.CRC].includes(
                              currentClaimForm?.info.countryCode,
                            ) &&
                              ![Rol.CUSTOMER].includes(
                                Number(localStorage.getItem('rolId')),
                              ) && (
                                <>
                                  <Button
                                    key="btnPdfbutton"
                                    color="secondary"
                                    text="Enviar PDF"
                                    onClick={() => sendPdf()}
                                  ></Button>
                                  |
                                </>
                              )}

                            {(currentClaimForm?.info.countryCode ===
                              CountryCode.PAN ||
                              currentClaimForm?.info.countryCode ===
                                CountryCode.PRI) && (
                              <>
                                <Button
                                  key="btnActiveInspection"
                                  color="secondary"
                                  text="Habilitar Enlace"
                                  onClick={() => {
                                    if (
                                      currentClaimForm?.info.countryCode ===
                                      CountryCode.PAN
                                    ) {
                                      triggerActiveLink();
                                    } else {
                                      setConfirmActiveLink(true);
                                    }
                                  }}
                                ></Button>
                                |
                              </>
                            )}

                            {currentClaimForm?.info.countryCode ===
                              CountryCode.PRI && (
                              <>
                                <Button
                                  text="Cartas"
                                  color="secondary"
                                  onClick={() => setIsLettersModalOpen(true)}
                                />
                                |
                              </>
                            )}
                            {
                              <>
                                {/* Letters ("Cartas") Action Menu Modal - START */}
                                <Modal open={isLettersModalOpen}>
                                  <Box sx={letterStyling.mainModal}>
                                    {canGoBack && (
                                      <IconButton
                                        onClick={handleGoBack}
                                        sx={{
                                          position: 'absolute',
                                          top: 8,
                                          right: 64,
                                          color: 'grey.500',
                                        }}
                                      >
                                        <ArrowBackIcon />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      onClick={resetMainModal}
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: 'grey.500',
                                      }}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                    <Typography
                                      id="modal-modal-title"
                                      variant="h5"
                                      component="h2"
                                    >
                                      Cartas {modalTitle}
                                    </Typography>
                                    <br></br>
                                    {/* Main Menu - START */}
                                    {!isTemplateMenuSelected && (
                                      <>
                                        <Button
                                          style={{ width: '100%' }}
                                          text="Ver Plantillas de Cartas"
                                          onClick={openTemplateMenu}
                                        ></Button>
                                        <br></br>
                                        <br></br>
                                        <Typography
                                          id="modal-modal-title"
                                          variant="h6"
                                          component="h3"
                                        >
                                          Cartas Enviadas
                                        </Typography>
                                        {sentLetters.length > 0 ? (
                                          <>
                                            <br></br>
                                            <SentLettersList
                                              sentLetters={sentLetters}
                                            />
                                            {/* Sent Letter Preview - START */}
                                            <Modal
                                              open={isSentLetterPreviewClicked}
                                            >
                                              <Box
                                                sx={letterStyling.loadingModal}
                                              >
                                                <div style={{ height: '100%' }}>
                                                  <Box
                                                    display="flex"
                                                    flexDirection="column"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    height="100%"
                                                  >
                                                    <p>Cargando...</p>
                                                    <CircularProgress />
                                                  </Box>
                                                </div>
                                              </Box>
                                            </Modal>
                                            {/* Sent Letter Preview - END */}
                                            {/* Sent Letter Download - START */}
                                            <Modal
                                              open={isSentLetterDownloadClicked}
                                            >
                                              <Box
                                                sx={letterStyling.loadingModal}
                                              >
                                                {isSentLetterDownloading ? (
                                                  <div
                                                    style={{ height: '100%' }}
                                                  >
                                                    <Box
                                                      display="flex"
                                                      flexDirection="column"
                                                      justifyContent="center"
                                                      alignItems="center"
                                                      height="100%"
                                                    >
                                                      <p>
                                                        Descargando carta...
                                                      </p>
                                                      <CircularProgress />
                                                    </Box>
                                                  </div>
                                                ) : (
                                                  <div
                                                    style={{ height: '100%' }}
                                                  >
                                                    <Box
                                                      display="flex"
                                                      flexDirection="column"
                                                      justifyContent="center"
                                                      alignItems="center"
                                                      height="100%"
                                                    >
                                                      <Alert severity="success">
                                                        Carta descargada
                                                      </Alert>
                                                      <br></br>
                                                      <Button
                                                        text="Finalizar"
                                                        onClick={() =>
                                                          setIsSentLetterDownloadClicked(
                                                            false,
                                                          )
                                                        }
                                                      />
                                                    </Box>
                                                  </div>
                                                )}
                                              </Box>
                                            </Modal>
                                            {/* Sent Letter Download - END */}
                                          </>
                                        ) : (
                                          <>
                                            <br />
                                            {isSentLettersFailing ? (
                                              <Alert severity="warning">
                                                <AlertTitle>Aviso</AlertTitle>
                                                En este momento no se pueden
                                                verificar las cartas y
                                                documentos enviados. Hubo un
                                                error al intentar conectarse con{' '}
                                                <b>Amazon S3</b>. Por favor,
                                                inténtelo de nuevo más tarde. Si
                                                el problema persiste, póngase en
                                                contacto con el soporte técnico.
                                              </Alert>
                                            ) : (
                                              <Alert severity="info">
                                                <AlertTitle>
                                                  No se han enviado cartas
                                                </AlertTitle>
                                                No se han enviado cartas o
                                                documentos aún. Accede a{' '}
                                                <i>Ver Plantillas de Cartas</i>{' '}
                                                para descargar o enviar cartas y
                                                documentos.
                                              </Alert>
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}
                                    {/* Main Menu - END */}
                                    {/* Letter Templates Flow - START */}
                                    {
                                      <>
                                        {isTemplateMenuSelected && (
                                          <>
                                            {!isLetterSelected && (
                                              <div>
                                                {/* Templates Selection Menu - START */}
                                                {letterTemplates.length > 0 ? (
                                                  letterTemplates.map(
                                                    (template) => (
                                                      <MenuItem
                                                        key={template.name}
                                                        onClick={() =>
                                                          selectLetterTemplate(
                                                            template,
                                                          )
                                                        }
                                                      >
                                                        <ListItemText
                                                          primary={
                                                            template.spanishName
                                                          }
                                                          secondary={
                                                            template.description
                                                          }
                                                        />
                                                      </MenuItem>
                                                    ),
                                                  )
                                                ) : (
                                                  <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    height="100%"
                                                  >
                                                    <Typography
                                                      variant="body1"
                                                      color="textSecondary"
                                                    >
                                                      No hay plantillas de
                                                      cartas disponibles.
                                                    </Typography>
                                                  </Box>
                                                )}
                                                {/* Templates Selection Menu - END */}
                                              </div>
                                            )}
                                            {/* Template Edit Form - START */}
                                            {isLetterSelected && (
                                              <div style={{ height: '100%' }}>
                                                {claimIdForLetter ? (
                                                  prefilledLetter &&
                                                  prefillMatchesSelection ? (
                                                    <div>
                                                      <Alert severity="info">
                                                        <AlertTitle>
                                                          Importante
                                                        </AlertTitle>
                                                        Importante: en la vista
                                                        previa siempre se
                                                        mostrará la carta
                                                        correspondiente al
                                                        asegurado. Sin embargo,
                                                        al enviar o descargar el
                                                        PDF, se generará
                                                        correctamente la
                                                        plantilla de la persona
                                                        que hayas seleccionado
                                                        (asegurado o conductor)
                                                        con los datos adecuados.
                                                      </Alert>
                                                      <br />
                                                      <div
                                                        style={{
                                                          display: 'flex',
                                                          gap: '20px',
                                                        }}
                                                      >
                                                        <div
                                                          style={{ flex: '1' }}
                                                        >
                                                          <LetterReviewScreen />
                                                        </div>
                                                        <div
                                                          style={{ flex: '2' }}
                                                        >
                                                          <iframe
                                                            src={`data:application/pdf;base64,${prefilledLetter.pdfPreview}#toolbar=0&view=FitH`}
                                                            width="100%"
                                                            height="100%"
                                                            title="PDF Preview"
                                                          />
                                                        </div>
                                                      </div>

                                                      {/* Template Download Modal - START */}
                                                      <Modal
                                                        open={
                                                          isTemplateLetterDownloadClicked
                                                        }
                                                      >
                                                        <Box
                                                          sx={
                                                            letterStyling.loadingModal
                                                          }
                                                        >
                                                          {isTemplateLetterDownloading ? (
                                                            <Box
                                                              display="flex"
                                                              flexDirection="column"
                                                              justifyContent="center"
                                                              alignItems="center"
                                                              height="100%"
                                                            >
                                                              <p>
                                                                Descargando
                                                                carta...
                                                              </p>
                                                              <CircularProgress />
                                                            </Box>
                                                          ) : (
                                                            <Box
                                                              display="flex"
                                                              flexDirection="column"
                                                              justifyContent="center"
                                                              alignItems="center"
                                                              height="100%"
                                                            >
                                                              <Alert severity="success">
                                                                Carta descargada
                                                              </Alert>
                                                              <br></br>
                                                              <Button
                                                                text="Finalizar"
                                                                onClick={() =>
                                                                  setIsTemplateLetterDownloadClicked(
                                                                    false,
                                                                  )
                                                                }
                                                              />
                                                            </Box>
                                                          )}
                                                        </Box>
                                                      </Modal>
                                                      {/* Template Download Modal - END */}

                                                      {/* Template Send Modal - START */}
                                                      <Modal
                                                        open={
                                                          isTemplateLetterSendClicked
                                                        }
                                                      >
                                                        <Box
                                                          sx={
                                                            letterStyling.loadingModal
                                                          }
                                                        >
                                                          {isTemplateLetterSending ? (
                                                            <Box
                                                              display="flex"
                                                              flexDirection="column"
                                                              justifyContent="center"
                                                              alignItems="center"
                                                              height="100%"
                                                            >
                                                              <p>
                                                                Enviando
                                                                carta...
                                                              </p>
                                                              <CircularProgress />
                                                            </Box>
                                                          ) : (
                                                            <Box
                                                              display="flex"
                                                              flexDirection="column"
                                                              justifyContent="center"
                                                              alignItems="center"
                                                              height="100%"
                                                            >
                                                              <Alert severity="success">
                                                                Carta enviada
                                                              </Alert>
                                                              <br></br>
                                                              <Button
                                                                text="Finalizar"
                                                                onClick={() =>
                                                                  setIsTemplateLetterSendClicked(
                                                                    false,
                                                                  )
                                                                }
                                                              />
                                                            </Box>
                                                          )}
                                                        </Box>
                                                      </Modal>
                                                      {/* Template Send Modal - END */}
                                                    </div>
                                                  ) : (
                                                    <Box
                                                      display="flex"
                                                      flexDirection="column"
                                                      justifyContent="center"
                                                      alignItems="center"
                                                      height="100%"
                                                    >
                                                      <p>
                                                        Cargando, por favor
                                                        espere...
                                                      </p>
                                                      <CircularProgress />
                                                    </Box>
                                                  )
                                                ) : (
                                                  <Box
                                                    display="flex"
                                                    justifyContent="center"
                                                    alignItems="center"
                                                    height="100%"
                                                  >
                                                    <Typography
                                                      variant="body1"
                                                      color="textSecondary"
                                                    >
                                                      No se encontró ninguna
                                                      reclamación vinculada para
                                                      generar cartas.
                                                    </Typography>
                                                  </Box>
                                                )}
                                              </div>
                                            )}
                                            {/* Template Edit Form - END */}
                                          </>
                                        )}
                                      </>
                                    }
                                    {/* Letter Templates Flow - END */}
                                  </Box>
                                </Modal>
                                {/* Error Catching Modal - START */}
                                <Modal
                                  open={isLettersModalOpen && isErrorModalOpen}
                                >
                                  <Box sx={letterStyling.mainModal}>
                                    <IconButton
                                      onClick={resetMainModal}
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        color: 'grey.500',
                                      }}
                                    >
                                      <CloseIcon />
                                    </IconButton>
                                    <Typography
                                      id="modal-modal-title"
                                      variant="h5"
                                      component="h2"
                                    >
                                      Ha ocurrido un error...
                                    </Typography>
                                    <Box
                                      display="flex"
                                      justifyContent="center"
                                      alignItems="center"
                                      height="100%"
                                    >
                                      <Typography
                                        variant="body1"
                                        color="textSecondary"
                                        sx={{ whiteSpace: 'pre' }}
                                      >
                                        <Alert severity="warning">
                                          <AlertTitle>Aviso</AlertTitle>
                                          {errorAlert.message}
                                        </Alert>
                                        <br></br>
                                        {errorAlert?.error && (
                                          <RequestErrorAlert
                                            error={errorAlert.error}
                                          />
                                        )}
                                        <br></br>
                                        <Box
                                          display="flex"
                                          justifyContent="center"
                                          mt={3}
                                        >
                                          <Button
                                            onClick={resetMainModal}
                                            variant="contained"
                                            text="Regresar"
                                            fullWidth
                                          />
                                        </Box>
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Modal>
                                {/* Error Catching Modal - END */}
                                {/* Letters ("Cartas") Action Menu Modal - END */}
                              </>
                            }

                            {currentClaimForm?.info?.sicStatusId >=
                              TransitionStatesIds.COMPLETED &&
                              ![Rol.CUSTOMER].includes(
                                Number(localStorage.getItem('rolId')),
                              ) && (
                                <>
                                  <Button
                                    text="Generar PDFs"
                                    color="secondary"
                                    onClick={handleGeneratePDF}
                                    disabled={
                                      sections.stageName ===
                                        'OPTIONS_AGREEMENT' &&
                                      legacyCurrentClaim?.previousPayment ===
                                        true &&
                                      (currentClaimForm?.info
                                        ?.currentStepStatus ===
                                        StepStatus.PENDING_PAYMENT ||
                                        currentClaimForm?.info
                                          ?.currentStepStatus ===
                                          StepStatus.PENDING_APPRO_AGREE)
                                    }
                                  />
                                  |
                                </>
                              )}

                            {(() => {
                              const hasPreviousPayment =
                                sections.stageName === 'OPTIONS_AGREEMENT' &&
                                legacyCurrentClaim?.previousPayment === true &&
                                (currentClaimForm?.info?.currentStepStatus ===
                                  StepStatus.PENDING_PAYMENT ||
                                  currentClaimForm?.info?.currentStepStatus ===
                                    StepStatus.PENDING_APPRO_AGREE);
                              const buttonDisabled =
                                isDisabledButton ||
                                !isValid ||
                                !enabledByStates ||
                                isFinishDisableByCondition ||
                                pendingApproval ||
                                hasPreviousPayment;

                              console.log('[Button Disabled Debug]', {
                                isDisabledButton,
                                isValid,
                                enabledByStates,
                                isFinishDisableByCondition,
                                pendingApproval,
                                buttonDisabled,
                                disabledReasons: {
                                  isDisabledButton,
                                  '!isValid': !isValid,
                                  '!enabledByStates': !enabledByStates,
                                  isFinishDisableByCondition,
                                  pendingApproval,
                                },
                              });

                              return (
                                <Button
                                  disabled={buttonDisabled}
                                  text={
                                    sections.submitTitle
                                      ? `Finalizar ${sections.submitTitle}`
                                      : 'Finalizado'
                                  }
                                  onClick={handleStageButtonClick}
                                  style={customStyle}
                                />
                              );
                            })()}

                            {showButtonPendingInfo && (
                              <>
                                |
                                <Button
                                  id="RequireInformation"
                                  text={'Pendiente Información'}
                                  onClick={handleClickOptionStep}
                                  style={customStyle}
                                  disabled={
                                    sections.stageName ===
                                      'OPTIONS_AGREEMENT' &&
                                    legacyCurrentClaim?.previousPayment ===
                                      true &&
                                    (currentClaimForm?.info
                                      ?.currentStepStatus ===
                                      StepStatus.PENDING_PAYMENT ||
                                      currentClaimForm?.info
                                        ?.currentStepStatus ===
                                        StepStatus.PENDING_APPRO_AGREE)
                                  }
                                />
                              </>
                            )}

                            {showButtonObjeted && (
                              <>
                                |
                                <Button
                                  id="Objeted"
                                  text={'Reconsiderar'}
                                  onClick={handleClickOptionStep}
                                  style={customStyle}
                                  disabled={
                                    sections.stageName ===
                                      'OPTIONS_AGREEMENT' &&
                                    legacyCurrentClaim?.previousPayment ===
                                      true &&
                                    (currentClaimForm?.info
                                      ?.currentStepStatus ===
                                      StepStatus.PENDING_PAYMENT ||
                                      currentClaimForm?.info
                                        ?.currentStepStatus ===
                                        StepStatus.PENDING_APPRO_AGREE)
                                  }
                                />
                              </>
                            )}

                            {showButtonNotApplicable && (
                              <>
                                |
                                <Button
                                  id="IsClaimNotApplicable"
                                  text={'Objetado / No Procede'}
                                  onClick={handleClickOptionStep}
                                  style={customStyle}
                                  disabled={
                                    sections.stageName ===
                                      'OPTIONS_AGREEMENT' &&
                                    legacyCurrentClaim?.previousPayment ===
                                      true &&
                                    (currentClaimForm?.info
                                      ?.currentStepStatus ===
                                      StepStatus.PENDING_PAYMENT ||
                                      currentClaimForm?.info
                                        ?.currentStepStatus ===
                                        StepStatus.PENDING_APPRO_AGREE)
                                  }
                                />
                              </>
                            )}
                          </Box>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                  <Box className="claim-sections__content-accordion">
                    {sections.sections
                      .map((item: any) => {
                        return { ...item };
                      })
                      .map((item: any) => {
                        const {
                          id,
                          title,
                          constraints,
                          containers,
                          dependencyJson,
                        } = item;

                        const showSection = checkFieldDependencySection(
                          dependencyJson,
                        );

                        const hasError = containers.some((container: any) =>
                          container.fields.some((field: any) => {
                            const key = field.modelBinding;
                            if (!key) {
                              return false;
                            }

                            const validationKey = key.includes('.')
                              ? key.replace(/\./g, '&')
                              : key;

                            const isError = Object.prototype.hasOwnProperty.call(
                              errors,
                              validationKey,
                            );

                            if (!showSection && isError) {
                              const {
                                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                                [validationKey]: _,
                                ...updatedErrors
                              } = errors;

                              setErrors(updatedErrors);
                            }

                            return isError;
                          }),
                        );

                        return (
                          constraints.isVisible &&
                          showSection &&
                          (item.targetSection !== FieldTypes.DEPRECIATION ? (
                            <Accordion
                              key={id}
                              className="accordion"
                              expanded={expanded[id] || hasError || false}
                              onChange={handleChange(id)}
                            >
                              <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${id}-content`}
                                id={`panel${id}-header`}
                              >
                                <Typography
                                  className={`typography ${
                                    hasError ? 'hasError' : ''
                                  }`}
                                >
                                  {title}
                                </Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                {targetSection(
                                  item.targetSection,
                                  item,
                                  errors,
                                  touched,
                                  setErrors,
                                )}
                              </AccordionDetails>
                            </Accordion>
                          ) : null)
                        );
                      })}
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Box>
      )}

      <Dialog open={showConfirmSubmit} style={{ fontFamily: 'Connect' }}>
        <DialogTitle
          id="confirm-restore-title"
          style={{ color: '#001d3d', fontFamily: 'Connect' }}
        >
          Confirmar Paso
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="confirm-step-modal"
            style={{ width: '450px', minHeight: '120px' }}
          >
            <br />
            <a className="modal-confirm-title">
              ¿Está seguro que desea aplicar la finalización de este paso?
            </a>
            <br />
            {optionsStep.IsClaimNotApplicable && (
              <div style={{ marginTop: '10px' }}>
                <FormControl fullWidth>
                  <InputLabel id="label-objeted-status">
                    Motivo de objeción (No Procede)
                  </InputLabel>
                  <Select
                    labelId="label-objeted-status"
                    id="objeted-status-select"
                    label="Motivo de objeción (No Procede)"
                    value={reasonStatusChange || ''}
                    onChange={(event: any) =>
                      setReasonStatusChange(event.target.value)
                    }
                  >
                    {Object.entries(ObjetedStatus)
                      .sort((a, b) => a[1].localeCompare(b[1])) // Ordenar alfabéticamente por el valor
                      .map(([key, value]) => (
                        <MenuItem key={key} value={value}>
                          {value}
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </div>
            )}
            {optionsStep.Objeted && (
              <>
                <br />
                <TextField
                  className="modal-confirm-input"
                  id="input-not-applicable"
                  label="Ingrese los detalles del evento estado no procede"
                  type="text"
                  required
                  multiline
                  fullWidth
                  value={reasonStatusChange}
                  onChange={(event: any) =>
                    setReasonStatusChange(event.target?.value)
                  }
                ></TextField>
              </>
            )}
            {(optionsStep.Objeted || optionsStep.ChangeStatusRevert) && (
              <div style={{ marginTop: '10px' }}>
                <FormControl fullWidth>
                  <InputLabel id="label-status">Estado *</InputLabel>
                  <Select
                    labelId="label-status"
                    id="demo-simple-select"
                    label="Estado *"
                    onChange={handleChangeStatus}
                  >
                    {listStatus[currentClaimForm?.info?.countryCode]?.map(
                      (status, index) => {
                        return (
                          Number(
                            StatusIds[
                              currentClaimForm?.info?.currentStepStatus
                            ],
                          ) > status.newStatus.StatusId && (
                            <MenuItem
                              key={index}
                              value={`${status.newStatus.StageId}-${status.newStatus.StatusId}`}
                            >
                              {status.text}
                            </MenuItem>
                          )
                        );
                      },
                    )}
                  </Select>
                </FormControl>
              </div>
            )}
            <br></br>
            {optionsStep.RequireInformation && (
              <>
                {(currentClaimForm?.info.countryCode === CountryCode.PAN ||
                  currentClaimForm?.info.countryCode === CountryCode.PRI) && (
                  <FormControl fullWidth margin="normal" required>
                    <InputLabel id="reason-select-label">
                      Seleccione un motivo
                    </InputLabel>
                    <Select
                      labelId="reason-select-label"
                      id="reasonSelect"
                      value={reasonSelect}
                      onChange={(event: any) => {
                        setReasonSelect(event.target.value);
                      }}
                      label="Seleccione un motivo"
                    >
                      <MenuItem value="">
                        <em>Seleccione un motivo</em>
                      </MenuItem>
                      {reasonPendingInfo?.map((reason) => (
                        <MenuItem key={reason.id} value={reason.reason}>
                          {reason.reason}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Autocomplete
                  style={{ margin: '0px' }}
                  multiple
                  fullWidth
                  id="pending-info-reasons-autocomplete"
                  options={pendingReasons}
                  getOptionLabel={(option) => option.name}
                  value={
                    textNotification
                      ? pendingReasons.filter((reason) =>
                          textNotification.split(', ').includes(reason.name),
                        )
                      : []
                  }
                  onChange={(_, newValue) => {
                    const selectedNames = newValue.map((reason) => reason.name);
                    setTextNotification(selectedNames.join(', '));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Razones de la información pendiente"
                      placeholder="Buscar y seleccionar razones..."
                      required
                      margin="normal"
                      fullWidth
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={option.name}
                        size="small"
                      />
                    ))
                  }
                  filterSelectedOptions
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                />
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            text="Cancelar"
            variant="outlined"
            color="secondary"
            onClick={() => {
              setShowConfirmSubmit(false);
              initOptionStep();
              setReasonStatusChange('');
              setStatusSelected(undefined);
              setTextNotification('');
            }}
          />
          <Button
            disabled={
              isDisabledButtonConfirm ||
              (optionsStep.RequireInformation &&
                (((currentClaimForm?.info.countryCode === CountryCode.PAN ||
                  currentClaimForm?.info.countryCode === CountryCode.PRI) &&
                  !reasonSelect) ||
                  !textNotification))
            }
            text="Sí, confirmar"
            variant="contained"
            color="primary"
            onClick={() => {
              handleSubmit(true);
            }}
          />
        </DialogActions>
      </Dialog>

      <DialogComponent
        messageStyle={{ fontSize: '16px' }}
        message={openDialogMsg}
        open={openDialog}
        handleClose={handleCloseDialog}
      />

      {isDetectedChanges && (
        <Stack sx={{ width: '100%' }} spacing={2} className="alertSavedChanges">
          <Alert severity="info">
            <AlertTitle>
              <strong>Hemos detectado cambios</strong>
            </AlertTitle>
            <Grid container spacing={2}>
              <Grid item md={8}>
                <p>¿Deseas guardar los cambios realizados en este paso?</p>
              </Grid>
              <Grid item md={4} sx={{ textAlign: 'right' }}>
                <Button
                  text="Guardar cambios"
                  type="buttom"
                  style={customStyle}
                  onClick={() => handleSubmit(false)}
                />
              </Grid>
            </Grid>
          </Alert>
        </Stack>
      )}
      <Dialog
        open={confirmActiveLink}
        onClose={() => setConfirmActiveLink(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Acceso a Formulario de Inspección
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            ¿Desea activar el enlace externo para acceder al formulario de
            inspección?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setConfirmActiveLink(false)}
            color="secondary"
            text="Cancelar"
          ></Button>
          <Button
            variant="contained"
            onClick={triggerActiveLink}
            color="primary"
            text="Confirmar"
          ></Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ClaimSections;
